import { PayloadAction } from '@reduxjs/toolkit';

import {
  Condition,
  ConditionAddress,
  ConditionChild,
  conditionGroupChildenKey,
  DesignState,
  MediaElement,
} from 'editor/src/store/design/types';

/**
 * Second half of a v2 condition import (see importConditionOperation):
 * inserts every sub-condition the file carried (already given fresh ids and
 * `parent` pointers), sets each condition's option buckets to the children
 * the file described (own elements + sub-conditions, also with fresh ids),
 * appends every imported element to the target spread's media, and — for
 * elements that sat in the top-level bucket in the source design — appends
 * them there too. Runs right after importConditionAction, in the same undo
 * step; the root condition's own replace (id/options) is that action's job,
 * not this one's.
 */
const importConditionLinkedContentReducer = (
  state: DesignState,
  action: PayloadAction<{
    address: ConditionAddress;
    conditionsToInsert: Condition[];
    bucketChildren: { bucketKey: string; children: ConditionChild[] }[];
    newElements: MediaElement[];
    topBucketAdditions: ConditionChild[];
  }>,
) => {
  const { address, conditionsToInsert, bucketChildren, newElements, topBucketAdditions } = action.payload;
  const spread = state.designData?.spreads[address.spreadIndex];
  const conditionGroup = spread?.conditionGroup;
  if (!spread || !conditionGroup) {
    return;
  }

  conditionsToInsert.forEach((condition) => {
    conditionGroup.conditions[condition.id] = condition;
  });

  bucketChildren.forEach(({ bucketKey, children }) => {
    conditionGroup.children[bucketKey] = children;
  });

  if (!spread.pages[0].groups.media) {
    spread.pages[0].groups.media = [];
  }
  const { media } = spread.pages[0].groups;
  newElements.forEach((element) => media!.push(element));

  if (topBucketAdditions.length) {
    conditionGroup.children[conditionGroupChildenKey] = [
      ...(conditionGroup.children[conditionGroupChildenKey] ?? []),
      ...topBucketAdditions,
    ];
  }
};

export default importConditionLinkedContentReducer;
